// src/pages/DisclaimerPage.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'April 2026';
const Section = ({ title, children }) => (
  <section style={s.section}><h2 style={s.sectionTitle}>{title}</h2>{children}</section>
);
const P = ({ children }) => <p style={s.para}>{children}</p>;

export default function DisclaimerPage() {
  useEffect(() => { document.title = 'Disclaimer — OhAPotato'; window.scrollTo(0, 0); }, []);
  return (
    <div style={s.page}>
      <nav style={s.nav}><Link to="/" style={s.navHome}>← Back to OhAPotato</Link></nav>
      <main style={s.main}>
        <header style={s.header}>
          <h1 style={s.title}>Disclaimer</h1>
          <p style={s.meta}>Last updated: {LAST_UPDATED}</p>
          <p style={s.intro}>
            The information provided on OhAPotato is for general informational and educational purposes only.
            Please read this disclaimer carefully before using the service.
          </p>
        </header>

        <Section title="Food safety and dietary advice">
          <P>The recipes and cooking information provided on OhAPotato are generated by automated systems, third-party recipe databases, and artificial intelligence. While we strive for accuracy, we cannot guarantee that all recipes are complete, accurate, or safe for all individuals.</P>
          <P>OhAPotato does not provide medical, dietary, or nutritional advice. If you have a food allergy, dietary restriction, medical condition, or specific nutritional requirement, always consult a qualified healthcare professional or registered dietitian before making dietary changes based on recipes found on this site.</P>
          <P>Always exercise caution and good judgement when preparing food. Ensure proteins are cooked to safe internal temperatures. Check all ingredients for potential allergens before use. The site operators accept no liability for any adverse outcomes resulting from recipes prepared using this service.</P>
        </Section>

        <Section title="Nutritional information">
          <P>Nutritional data displayed on OhAPotato is estimated and provided for general guidance only. Actual nutritional content may vary based on specific ingredients used, preparation methods, portion sizes, and ingredient variations. These figures should not be used to manage a medical condition or calculate precise dietary intake for clinical purposes.</P>
        </Section>

        <Section title="Recipe accuracy">
          <P>Recipes are sourced from third-party databases and AI generation systems. We do not independently verify the accuracy, completeness, or quality of every recipe. Some recipes may contain errors, omissions, or regional variations in ingredient names and measurements. Always use your own judgement and culinary experience when preparing recipes.</P>
        </Section>

        <Section title="Advertising disclosure">
          <P>OhAPotato may display advertisements served by Google AdSense and other advertising partners. The presence of advertising does not imply endorsement of any product or service. Advertising content is independent of editorial content and is selected by advertising networks based on site context and user signals.</P>
          <P>We do not receive payment to feature specific recipes, ingredients, products, or brands in our editorial content or cooking guides. All cooking guides and articles are written independently.</P>
        </Section>

        <Section title="Affiliate links">
          <P>OhAPotato does not currently use affiliate links. If this changes in the future, affiliate relationships will be clearly disclosed in accordance with applicable regulations.</P>
        </Section>

        <Section title="Third-party content">
          <P>Recipe content displayed in search results is sourced from third-party providers including Edamam and Spoonacular. Copyright in that content belongs to the original recipe creators. We link to original sources wherever possible. If you believe your content has been displayed incorrectly or without proper attribution, please <Link to="/contact" style={s.link}>contact us</Link>.</P>
        </Section>

        <Section title="Artificial intelligence">
          <P>Some features of OhAPotato, including Chef Mode recipe insights and AI-generated recipe suggestions, use Google Gemini and other AI language models. AI-generated content may contain inaccuracies and should be treated as a starting point for your own research and judgement, not as authoritative culinary advice.</P>
        </Section>

        <Section title="External links">
          <P>Recipe cards on OhAPotato may link to external websites. We have no control over the content or privacy practices of those websites and accept no responsibility for them. Links to external sites do not imply endorsement.</P>
        </Section>

        <Section title="Changes to this disclaimer">
          <P>We reserve the right to update this disclaimer at any time. The date at the top of this page indicates when it was last revised. Continued use of the service constitutes acceptance of the current disclaimer.</P>
        </Section>

        <Section title="Contact">
          <P>If you have any questions about this disclaimer, please <Link to="/contact" style={s.link}>contact us</Link>.</P>
        </Section>

        <footer style={s.footer}>
          <Link to="/" style={s.footerLink}>Home</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/terms" style={s.footerLink}>Terms of Service</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/contact" style={s.footerLink}>Contact</Link>
        </footer>
      </main>
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'var(--bg-base)', color:'var(--text-primary)', transition:'background 0.3s, color 0.3s' },
  nav: { padding:'16px 32px', borderBottom:'1px solid var(--border-base)' },
  navHome: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--accent-orange)', textDecoration:'none', fontWeight:'600' },
  main: { maxWidth:'720px', margin:'0 auto', padding:'48px 24px 80px' },
  header: { marginBottom:'48px', paddingBottom:'28px', borderBottom:'1px solid var(--border-base)' },
  title: { fontFamily:'var(--font-display)', fontSize:'clamp(26px,4vw,36px)', fontWeight:'700', marginBottom:'8px', letterSpacing:'-0.4px' },
  meta: { fontFamily:'var(--font-body)', fontSize:'12px', color:'var(--text-muted)', marginBottom:'16px', textTransform:'uppercase', letterSpacing:'1px' },
  intro: { fontFamily:'var(--font-body)', fontSize:'16px', color:'var(--text-secondary)', lineHeight:'1.7' },
  section: { marginBottom:'40px' },
  sectionTitle: { fontFamily:'var(--font-display)', fontSize:'18px', fontWeight:'600', color:'var(--text-primary)', marginBottom:'14px', paddingBottom:'8px', borderBottom:'1px solid var(--border-base)' },
  para: { fontFamily:'var(--font-body)', fontSize:'15px', color:'var(--text-secondary)', lineHeight:'1.75', marginBottom:'14px' },
  link: { color:'var(--accent-orange)', textDecoration:'none' },
  footer: { marginTop:'64px', paddingTop:'24px', borderTop:'1px solid var(--border-base)', display:'flex', gap:'12px', alignItems:'center', justifyContent:'center', flexWrap:'wrap' },
  footerLink: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--text-muted)', textDecoration:'none' },
  footerDot: { color:'var(--text-muted)', fontSize:'11px' },
};
